import { Alert, AlertTitle, Box, Stack } from "@mui/material";
import * as Sentry from "@sentry/react";
import { Component, ErrorInfo, ReactNode } from "react";
import Button from "src/shared/components/Button";
import {
  forceReloadWithoutStaleServiceWorkers,
  isChunkLoadError,
  recoverFromChunkLoadError,
} from "src/utils/chunkLoadRecovery";

interface FlowErrorBoundaryProps {
  children: ReactNode;
  /** Name of the flow for Sentry context, e.g. "onboarding", "quiz", "resource" */
  flowName: string;
  /**
   * What to render once a child has crashed. Omit for the default full-width
   * "Something went wrong" card with retry — right for a whole route/flow.
   * Pass `null` to fail silently (an optional widget or overlay that chokes on
   * misshapen data should just not appear, not blank the page around it), or a
   * node for compact inline fallback text. The crash is still reported to
   * Sentry either way, so silent never means invisible to us.
   */
  fallback?: ReactNode;
}

interface FlowErrorBoundaryState {
  error: Error | null;
}

/**
 * Per-flow error boundary that catches component crashes and shows
 * a recoverable error UI instead of crashing the entire app.
 * Reports errors to Sentry with flow context.
 */
export default class FlowErrorBoundary extends Component<
  FlowErrorBoundaryProps,
  FlowErrorBoundaryState
> {
  constructor(props: FlowErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): FlowErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { flowName } = this.props;
    Sentry.withScope((scope) => {
      scope.setTag("flow", flowName);
      scope.setContext("componentStack", {
        stack: errorInfo.componentStack,
      });
      Sentry.captureException(error);
    });
    // A stale app shell requesting a rotated chunk hash lands here via
    // React.lazy. Resetting state can never recover it (lazy caches the
    // rejected import), so reload once with stale service workers dropped —
    // this is what un-sticks kiosk/facility devices that hold the old shell.
    recoverFromChunkLoadError(error);
  }

  handleRetry = (): void => {
    const { error } = this.state;
    if (error && isChunkLoadError(error)) {
      forceReloadWithoutStaleServiceWorkers();
      return;
    }
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error) {
      // A caller-supplied fallback (including `null`) wins over the default
      // card, so an optional widget can degrade to nothing instead of taking
      // down the surface that hosts it.
      if (fallback !== undefined) {
        return fallback;
      }
      return (
        <Box sx={{ p: 3, maxWidth: 600, mx: "auto", mt: 4 }}>
          <Alert severity="error">
            <AlertTitle>Something went wrong</AlertTitle>
            We encountered an unexpected error. You can try again or go back to
            the home page.
          </Alert>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button onClick={this.handleRetry}>Try Again</Button>
            <Button
              variant="outlined"
              onClick={() => {
                window.location.href = "/";
              }}
            >
              Go Home
            </Button>
          </Stack>
        </Box>
      );
    }

    return children;
  }
}
